import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import Layout from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { KeyRound, Copy, CheckCircle2, XCircle, Clock } from "lucide-react";

const STATUS_ICON = {
  pending: <Clock size={14} className="text-accent" />,
  approved: <CheckCircle2 size={14} className="text-primary" />,
  rejected: <XCircle size={14} className="text-destructive" />,
  used: <CheckCircle2 size={14} className="text-muted-foreground" />,
};

export default function AdminPasswordResets() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(null);
  const [approvedTokens, setApprovedTokens] = useState({}); // {id: {token,email}}

  const load = () => api.get("/admin/password-resets").then(r => setItems(r.data));
  useEffect(() => { load(); }, []);

  const approve = async (id) => {
    setBusy(id);
    try {
      const r = await api.post(`/admin/password-resets/${id}/approve`);
      if (r.data.token) {
        setApprovedTokens(t => ({ ...t, [id]: { token: r.data.token, email: r.data.user_email } }));
      }
      toast.success("Approved — share the link below with the student");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Approve failed"); }
    finally { setBusy(null); }
  };

  const reject = async (id) => {
    if (!window.confirm("Reject this reset request?")) return;
    setBusy(id);
    try {
      await api.post(`/admin/password-resets/${id}/reject`);
      toast.success("Rejected");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Reject failed"); }
    finally { setBusy(null); }
  };

  const buildLink = (token) => `${window.location.origin}/reset-password?token=${token}`;
  const copyLink = async (token) => {
    try {
      await navigator.clipboard.writeText(buildLink(token));
      toast.success("Reset link copied");
    } catch (e) {
      toast.error("Copy failed — select the link below and copy manually");
    }
  };

  const pending = items.filter(i => i.status === "pending");
  const done = items.filter(i => i.status !== "pending");

  return (
    <Layout>
      <div className="max-w-4xl mx-auto p-6 lg:p-12">
        <div className="label-caps mb-3 flex items-center gap-2"><KeyRound size={14} /> Password resets</div>
        <h1 className="font-serif text-4xl lg:text-6xl leading-none mb-8">Reset requests.</h1>

        <section className="mb-10">
          <h2 className="font-serif text-2xl mb-3">Awaiting review ({pending.length})</h2>
          {pending.length === 0 ? (
            <p className="text-sm text-muted-foreground">No pending reset requests.</p>
          ) : (
            <div className="space-y-3" data-testid="pending-resets-list">
              {pending.map(p => (
                <Card key={p.id} className="p-5" data-testid={`reset-row-${p.id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="label-caps mb-1 flex items-center gap-2">{STATUS_ICON.pending} Pending</div>
                      <div className="font-serif text-2xl">{p.user_name}</div>
                      <div className="text-xs text-muted-foreground">{p.user_email}</div>
                      <div className="text-[10px] text-muted-foreground mt-2">Requested {new Date(p.created_at).toLocaleString()}</div>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => reject(p.id)} disabled={busy === p.id} data-testid={`reject-reset-${p.id}`}>Reject</Button>
                      <Button size="sm" onClick={() => approve(p.id)} disabled={busy === p.id} data-testid={`approve-reset-${p.id}`}>Approve & generate link</Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </section>

        {items.filter(i => i.status === "approved").length > 0 && (
          <section className="mb-10">
            <h2 className="font-serif text-2xl mb-3">Recently approved — share the link</h2>
            <div className="space-y-3">
              {items.filter(i => i.status === "approved").map(p => {
                const stored = approvedTokens[p.id];
                const token = stored?.token || p.token; // list endpoint returns token; safe to show to admin
                const link = buildLink(token);
                return (
                  <Card key={p.id} className="p-5" data-testid={`approved-reset-${p.id}`}>
                    <div className="flex items-baseline justify-between mb-2 flex-wrap gap-2">
                      <div>
                        <div className="font-serif text-xl">{p.user_name}</div>
                        <div className="text-xs text-muted-foreground">{p.user_email}</div>
                      </div>
                      <Button size="sm" variant="outline" onClick={() => copyLink(token)} data-testid={`copy-link-${p.id}`}><Copy size={12} className="mr-1" /> Copy link</Button>
                    </div>
                    <div className="text-xs font-mono bg-secondary p-2 rounded break-all">{link}</div>
                    <div className="text-[10px] text-muted-foreground mt-2">Link valid 24 hours after approval.</div>
                  </Card>
                );
              })}
            </div>
          </section>
        )}

        <section>
          <h2 className="font-serif text-2xl mb-3">History</h2>
          {done.filter(i => i.status !== "approved").length === 0 ? (
            <p className="text-sm text-muted-foreground">No completed history yet.</p>
          ) : (
            <div className="space-y-2 opacity-80">
              {done.filter(i => i.status !== "approved").map(p => (
                <div key={p.id} className="p-3 border border-border rounded-md text-sm flex items-center justify-between">
                  <span className="flex items-center gap-2">{STATUS_ICON[p.status]} <strong>{p.user_name}</strong> — {p.status}</span>
                  <span className="text-xs text-muted-foreground">{new Date(p.used_at || p.approved_at || p.created_at).toLocaleString()}</span>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </Layout>
  );
}
